import { UpdateFormTemplateStatusDialog } from "#src/batteries-included-components/Dialogs/UpdateFormTemplateStatusDialog";
import { FormSubmissionsViewFilterArea } from "#src/batteries-included-components/FilterAreas/FormsFilterAreas";
import { FormSubmissionsTablePanel } from "#src/batteries-included-components/Panels/TablePanels/FormSubmissionsTablePanel";
import { UserDataDisplay } from "#src/components/Common/UserDataDisplay";
import { useActionDetails } from "#src/components/hooks/useActionDetails";
import {
  useHasPermission,
  useIsFeatureAvailable,
} from "#src/contexts/AuthenticatedContext.helpers";
import { useStorageKey } from "#src/hooks/useStorageKey";
import { useBreadcrumbs } from "#src/Routers/breadcrumbsHelper";
import { useNavigate, useParams, useSearchParams } from "#src/Routers/hooks";
import { FORMS_BREADCRUMB } from "#src/routes/forms";
import { FORMS_CATEGORIES_BREADCRUMB } from "#src/routes/forms/categories";
import {
  FORM_CATEGORY_DETAILS_BREADCRUMB,
  linkToFormCategoryDetails,
} from "#src/routes/forms/categories/[categoryId]";
import { FormSchemaStatusToPillVariantMapping } from "#src/view-models/forms";
import { useQuery } from "@tanstack/react-query";
import {
  Button,
  ButtonToggleGroup,
  generateDocumentTitle,
  KeyValuePanel,
  Link,
  Page,
  Pill,
} from "@validereinc/common-components";
import {
  FormCategoryAdapter,
  FormSchemaAdapter,
  ResourceDefinitions,
  Resources,
} from "@validereinc/domain";
import { toTitleCaseString } from "@validereinc/utilities";
import classNames from "classnames/bind";
import React, { useEffect, useState } from "react";
import { Titled } from "react-titled";
import { FORM_TEMPLATE_DETAILS_BREADCRUMB } from ".";
import { linkToEditFormCategoryFormTemplate } from "../../edit-form-template/[formSchemaId]";
import { linkToCreateFormSubmission } from "./create-form-submission";
import styles from "./FormTemplateDetailPage.module.scss";

const cx = classNames.bind(styles);

export const FormTemplateDetailsPage = () => {
  const { categoryId, formTemplateId } = useParams<{
    categoryId: string;
    formTemplateId: string;
  }>();
  const [canReadFormSchemas] = useHasPermission("form_schemas:read");
  const [canWriteFormSchemas] = useHasPermission("form_schemas:write");
  const [isEditFormTemplateButtonAvailable] = useIsFeatureAvailable({
    featureFlagQuery: "forms:schemas_create",
  });

  const [{ "submission-type": submissionsType }, setSearchParams] =
    useSearchParams();

  const [updateStatus, setUpdateStatus] = useState<
    "active" | "archived" | "draft" | undefined
  >(undefined);

  const [
    filterConfigStorageKey,
    tableConfigStorageKey,
    viewConfigStorageKey,
    tableStateStorageKey,
  ] = useStorageKey(
    [
      "filter-configuration",
      "table-configuration",
      "view-configuration",
      "table-state",
    ],
    ["forms", "templates", formTemplateId, "submissions"]
  );

  const navigate = useNavigate();

  const categoryQuery = useQuery({
    queryKey: [Resources.FORM_CATEGORY, categoryId],
    queryFn: async ({ queryKey }) => {
      const [_, id] = queryKey;

      return await FormCategoryAdapter.getOne({
        id,
      });
    },
    enabled: !!categoryId,
  });

  const templateQuery = useQuery({
    queryKey: [Resources.FORM_SCHEMA, formTemplateId],
    queryFn: async ({ queryKey }) => {
      const [_, formSchemaId] = queryKey;

      return await FormSchemaAdapter.getOne({
        id: formSchemaId,
      });
    },
    staleTime: 2 * 60 * 1000,
    select: (resp) => resp?.data,
    enabled: !!formTemplateId,
  });

  const createInformation = useActionDetails({
    type: "create",
    date: templateQuery.data?.created_at,
    byUserId: templateQuery.data?.created_by,
  });
  const editInformation = useActionDetails({
    type: "edit",
    date: templateQuery.data?.updated_at,
    byUserId: templateQuery.data?.updated_by,
  });

  const breadcrumbs = useBreadcrumbs(
    [
      FORMS_BREADCRUMB,
      FORMS_CATEGORIES_BREADCRUMB,
      FORM_CATEGORY_DETAILS_BREADCRUMB,
      FORM_TEMPLATE_DETAILS_BREADCRUMB,
    ],
    {
      2: categoryQuery?.data?.name,
      3: templateQuery?.data?.name,
    }
  );

  const setSubmissionType = (newSubmissionType: "all" | "draft") => {
    setSearchParams({ "submission-type": newSubmissionType });
  };

  useEffect(() => {
    if (!submissionsType) {
      setSubmissionType("all");
    }
  }, [submissionsType]);

  return (
    <Titled
      title={generateDocumentTitle(
        breadcrumbs.at(-1)?.title ?? FORM_TEMPLATE_DETAILS_BREADCRUMB.title,
        ResourceDefinitions.form_schema.label.plural
      )}
    >
      <Page
        title={
          templateQuery?.data ? (
            <>
              {templateQuery.data.name}&nbsp;
              <Pill
                variant={
                  FormSchemaStatusToPillVariantMapping[
                    templateQuery.data.status
                  ] ?? "default"
                }
                hasDot={false}
              >
                {toTitleCaseString(templateQuery.data.status, {
                  intent: "ui",
                  exceptions: ["oil-and-gas"],
                })}
              </Pill>
            </>
          ) : (
            ""
          )
        }
        category="Form Template"
        breadcrumbs={breadcrumbs}
        actionRow={[
          ...(templateQuery?.data?.status === "active"
            ? [
                <Button
                  key="archive-action"
                  variant="error-outline"
                  onClick={() => setUpdateStatus("archived")}
                >
                  Archive
                </Button>,
                canReadFormSchemas &&
                  canWriteFormSchemas &&
                  isEditFormTemplateButtonAvailable && (
                    <Button
                      key="edit-action"
                      onClick={() =>
                        navigate({
                          pathname: linkToEditFormCategoryFormTemplate(
                            categoryId,
                            formTemplateId
                          ),
                        })
                      }
                    >
                      Edit
                    </Button>
                  ),
                <Button
                  key="create-submission-action"
                  onClick={() => {
                    navigate({
                      pathname: linkToCreateFormSubmission(
                        categoryId,
                        formTemplateId
                      ),
                    });
                  }}
                  variant="primary"
                >
                  Create Submission
                </Button>,
              ]
            : []),
          ...(templateQuery?.data?.status === "archived"
            ? [
                <Button
                  key="activate-button"
                  onClick={() => setUpdateStatus("active")}
                >
                  Activate
                </Button>,
              ]
            : []),
        ]}
        renderMeta={
          !editInformation.description
            ? undefined
            : ({ MetaSegments }) => (
                <MetaSegments values={[editInformation.description]} />
              )
        }
      >
        <div className={cx("topContainer")}>
          <KeyValuePanel
            panelMaxColumnCount={2}
            panelKeyValueListContainerProps={{
              style: { padding: 0 },
            }}
            panelProps={{
              title: "Template Details",
              isFluidY: true,
              loaded: !templateQuery.isLoading,
            }}
            data={[
              {
                title: "Created Date",
                value: createInformation.formattedDate,
              },
              {
                title: "Created By",
                value:
                  templateQuery.data?.created_by &&
                  createInformation.userName ? (
                    <UserDataDisplay
                      id={templateQuery.data?.created_by}
                      name={createInformation.userName}
                      alignment="right"
                    />
                  ) : (
                    "-"
                  ),
              },
              {
                title: "Category",
                value: (
                  <Link
                    onClick={() => {
                      navigate({
                        pathname: linkToFormCategoryDetails(
                          categoryQuery?.data?.id
                        ),
                      });
                    }}
                    label={categoryQuery?.data?.name ?? "-"}
                  />
                ),
              },
              {
                title: "Description",
                value: templateQuery?.data?.description
                  ? templateQuery?.data.description.trim()
                  : "-",
              },
              {
                title: "Current Version",
                value: (
                  <div className={cx("versionPill")}>
                    <Pill variant="default">
                      {templateQuery?.data?.version}
                    </Pill>
                  </div>
                ),
              },
            ]}
          />
        </div>

        <div className={cx("filterContainer")}>
          <ButtonToggleGroup
            items={[
              { label: "Submissions", dataKey: "all" },
              { label: "Drafts", dataKey: "draft" },
            ]}
            activeKey={submissionsType}
            onChange={(newStatus: string) =>
              setSubmissionType(newStatus as "all" | "draft")
            }
            name="submission-type-switcher"
          />
          <FormSubmissionsViewFilterArea
            viewConfigStorageKey={viewConfigStorageKey}
          />
        </div>

        <FormSubmissionsTablePanel
          formSchemaId={formTemplateId}
          formCategoryId={categoryId}
          isDraft={submissionsType === "draft"}
          tableConfigStorageKey={tableConfigStorageKey}
          tableStateStorageKey={tableStateStorageKey}
          filterConfigStorageKey={filterConfigStorageKey}
          viewConfigStorageKey={viewConfigStorageKey}
        />

        <UpdateFormTemplateStatusDialog
          isOpen={!!updateStatus}
          onClose={() => setUpdateStatus(undefined)}
          formSchemaId={formTemplateId}
          status={updateStatus}
        />
      </Page>
    </Titled>
  );
};
